import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared/States";
import { Avatar } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Shield, Plus, Sparkles, MoreHorizontal, MailCheck, UserX, UserCheck, Trash2, Loader2, Users, Lock } from "lucide-react";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  invited: "bg-amber-50 text-amber-700",
  suspended: "bg-red-50 text-red-700",
};

export default function Admin() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [seats, setSeats] = useState(null);
  const [audit, setAudit] = useState({ items: [], ai_actions: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "Sales Rep" });
  const [busyId, setBusyId] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = async () => {
    const u = await api.get("/users");
    setUsers(u.data.items);
    setRoles(u.data.roles);
    setSeats(u.data.seats);
    const a = await api.get("/audit-logs");
    setAudit(a.data);
  };
  useEffect(() => { load(); }, []);

  const atSeatLimit = seats && seats.limit !== null && seats.used >= seats.limit;

  const invite = async () => {
    if (!form.email) return toast.error("Email required");
    setInviting(true);
    try {
      const { data } = await api.post("/users/invite", form);
      if (data.email_sent) toast.success(`Invitation emailed to ${form.email}`);
      else toast.warning(`Invite created, but the email couldn't be sent${data.email_error ? `: ${data.email_error}` : ""}`);
      setOpen(false);
      setForm({ name: "", email: "", role: "Sales Rep" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send invite";
      toast.error(detail, err?.response?.status === 402 ? {
        action: { label: "Upgrade Plan", onClick: () => navigate("/app/settings?tab=billing") },
      } : undefined);
    } finally { setInviting(false); }
  };

  const setRole = async (uid, role) => {
    try { await api.put(`/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update role"); }
  };

  const resendInvite = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      if (data.email_sent) toast.success(`Invite resent to ${data.email || u.email}`);
      else toast.warning(`Invite link refreshed, but the email couldn't be sent${data.email_error ? `: ${data.email_error}` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend invite");
    } finally { setBusyId(null); }
  };

  const toggleStatus = async (u) => {
    const next = u.membership_status === "suspended" ? "active" : "suspended";
    setBusyId(u.id);
    try {
      await api.put(`/users/${u.id}/status`, { status: next });
      toast.success(next === "suspended" ? `${u.name} suspended` : `${u.name} re-activated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update status");
    } finally { setBusyId(null); }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setBusyId(removeTarget.id);
    try {
      await api.delete(`/users/${removeTarget.id}`);
      toast.success(`${removeTarget.name} removed from workspace`);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove member");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader title="Administration" subtitle="Users, roles & audit logs" icon={Shield}
        actions={<Button data-testid="invite-user-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)} disabled={atSeatLimit}>
          {atSeatLimit ? <><Lock className="h-4 w-4 mr-1" />Seat limit reached</> : <><Plus className="h-4 w-4 mr-1" />Invite User</>}
        </Button>} />
      {seats && (
        <div data-testid="seat-usage-banner" className={`rounded-xl border p-3.5 mb-4 flex items-center justify-between gap-3 ${atSeatLimit ? "border-amber-200 bg-amber-50/70" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-2.5 text-sm">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="font-semibold text-slate-900">{seats.used}{seats.limit !== null ? ` / ${seats.limit}` : ""} seats used</span>
            <span className="text-slate-400">· {seats.plan || "Trial"} plan{seats.limit === null ? " (unlimited seats)" : ""}</span>
          </div>
          {atSeatLimit && <Button size="sm" variant="outline" onClick={() => navigate("/app/settings?tab=billing")} data-testid="upgrade-for-seats-button">Upgrade Plan</Button>}
        </div>
      )}
      <Tabs defaultValue="users">
        <TabsList><TabsTrigger value="users" data-testid="admin-tab-users">Users & Roles</TabsTrigger><TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger><TabsTrigger value="ai" data-testid="admin-tab-ai">AI Actions</TabsTrigger></TabsList>
        <TabsContent value="users" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden"><table className="w-full text-sm">
            <thead className="bg-slate-50/70"><tr className="text-left text-xs font-semibold text-slate-600">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 hidden sm:table-cell">Status</th>
              <th className="px-4 py-3 hidden md:table-cell">Last login</th>
              <th className="px-4 py-3 w-12"></th>
            </tr></thead>
            <tbody data-testid="admin-users-table-body">{(users || []).map((u) => {
              const isSelf = u.id === me?.id;
              const status = u.membership_status || "active";
              return (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={u.name} src={u.avatar} size={30} /><div><div className="font-medium">{u.name}{isSelf && <span className="ml-1.5 text-[10px] text-slate-400 font-normal">(you)</span>}</div><div className="text-xs text-slate-400">{u.email}</div></div></div></td>
                  <td className="px-4 py-3">
                    <select data-testid={`admin-role-select-${u.id}`} value={u.role} onChange={(e) => setRole(u.id, e.target.value)} disabled={isSelf} className="rounded-lg border border-slate-200 px-2 py-1 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30">
                      {roles.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <Badge data-testid={`admin-status-${u.id}`} variant="outline" className={`capitalize border-0 ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>{status}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-400">{u.last_login ? timeAgo(u.last_login) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {!isSelf && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`admin-user-actions-${u.id}`} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors" aria-label="Member actions">
                            {busyId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {status === "invited" && (
                            <DropdownMenuItem data-testid={`admin-resend-invite-${u.id}`} onClick={() => resendInvite(u)}>
                              <MailCheck className="h-4 w-4 mr-2" />Resend invite
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`admin-toggle-status-${u.id}`} onClick={() => toggleStatus(u)}>
                            {status === "suspended" ? <><UserCheck className="h-4 w-4 mr-2" />Re-activate</> : <><UserX className="h-4 w-4 mr-2" />Suspend</>}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`admin-remove-user-${u.id}`} className="text-red-600 focus:text-red-600" onClick={() => setRemoveTarget(u)}>
                            <Trash2 className="h-4 w-4 mr-2" />Remove from workspace
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
          {users && users.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No members yet.</div>}
          </div>
        </TabsContent>
        <TabsContent value="audit" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">{audit.items.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No audit entries yet.</div> : audit.items.map((a) => <div key={a.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0 text-sm"><span className="flex-1">{a.description || a.action}</span><span className="text-xs text-slate-400">{timeAgo(a.created_at)}</span></div>)}</div>
        </TabsContent>
        <TabsContent value="ai" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">{(audit.ai_actions || []).length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No AI actions logged yet.</div> : audit.ai_actions.map((a) => <div key={a.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0 text-sm"><Sparkles className="h-4 w-4 text-[#7C3AED]" /><span className="flex-1"><b className="capitalize">{a.kind}</b>: {a.input}</span><span className="text-xs text-slate-400">{timeAgo(a.created_at)}</span></div>)}</div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
          <div className="space-y-3"><div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div><div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div><div><Label>Role</Label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">{roles.map((r) => <option key={r}>{r}</option>)}</select></div></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="send-invite-button" className="aurelo-gradient text-white" onClick={invite} disabled={inviting}>{inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invite"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="admin-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes them from the workspace. Their user account is preserved and they can be re-invited later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-remove-confirm" className="bg-red-600 hover:bg-red-700 focus:ring-red-600" onClick={confirmRemove}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
